import { useCallback, useRef, useState } from 'react'
import { COMMENT_IMAGES_MAX } from '@crm/shared'
import { commentImageUrl, uploadCommentImage } from '../../../shared/api/comments.api'
import type { CommentImage } from '../../../shared/api/types'
import { apiErrorText } from '../../../shared/api/errorText'
import { toast } from '../../../shared/ui/toastStore'

/**
 * Картинки в комментариях (с 29.10.2026, docs/features/comment-images.md).
 *
 * Картинка грузится на сервер сразу — при вставке (Ctrl+V / Cmd+V) в поле
 * комментария или выборе файла скрепкой; к комментарию уходят их id. Одна
 * обвязка на все окна переписки: заявка, позиция, нетарифка, «Переписка по позиции».
 */

const MAX_BYTES = 5 * 1024 * 1024
const ACCEPT = ['image/png', 'image/jpeg', 'image/webp']

export interface CommentImagesState {
  images: CommentImage[]
  ids: string[]
  uploading: number
  addFiles: (files: File[]) => void
  remove: (id: string) => void
  clear: () => void
}

/** Черновые картинки комментария: загрузка, удаление из черновика, сброс после отправки. */
export function useCommentImages(): CommentImagesState {
  const [images, setImages] = useState<CommentImage[]>([])
  const [uploading, setUploading] = useState(0)
  const countRef = useRef(0)
  countRef.current = images.length + uploading

  const addFiles = useCallback((files: File[]) => {
    for (const file of files) {
      if (!ACCEPT.includes(file.type)) {
        toast.error('Можно приложить только картинку: png, jpg, webp')
        continue
      }
      if (file.size > MAX_BYTES) {
        toast.error('Картинка больше 5 МБ')
        continue
      }
      if (countRef.current >= COMMENT_IMAGES_MAX) {
        toast.error(`К комментарию — не больше ${COMMENT_IMAGES_MAX} картинок`)
        break
      }
      countRef.current += 1
      setUploading((n) => n + 1)
      uploadCommentImage(file)
        .then((img) => setImages((list) => [...list, img]))
        .catch((e) => toast.error(apiErrorText(e, 'Не удалось загрузить картинку')))
        .finally(() => setUploading((n) => n - 1))
    }
  }, [])

  const remove = useCallback((id: string) => {
    setImages((list) => list.filter((i) => i.id !== id))
  }, [])
  const clear = useCallback(() => setImages([]), [])

  return { images, ids: images.map((i) => i.id), uploading, addFiles, remove, clear }
}

/** Можно отправлять: есть текст или картинка, и ни одна картинка не догружается. */
export function canSendComment(text: string, state: CommentImagesState): boolean {
  return state.uploading === 0 && (text.trim().length > 0 || state.ids.length > 0)
}

/** Картинки из вставки (Ctrl+V): файлы-картинки буфера обмена; текст — не трогаем. */
export function imagesFromClipboard(e: React.ClipboardEvent): File[] {
  const files = Array.from(e.clipboardData?.files ?? []).filter((f) => f.type.startsWith('image/'))
  if (files.length > 0) e.preventDefault()
  return files
}

/** Скрепка рядом с кнопкой отправки — выбрать картинку с компьютера. */
export function AttachImageButton({
  state,
  disabled,
}: {
  state: CommentImagesState
  disabled?: boolean
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        title="Приложить картинку (или вставьте скриншот в поле: Ctrl+V)"
        aria-label="Приложить картинку"
        className="px-2 py-1.5 text-gray-500 rounded-lg hover:bg-gray-100 hover:text-gray-800 disabled:opacity-50"
      >
        <IconClip />
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT.join(',')}
        multiple
        hidden
        data-testid="comment-image-input"
        onChange={(e) => {
          state.addFiles(Array.from(e.target.files ?? []))
          e.target.value = ''
        }}
      />
    </>
  )
}

/** Картинки, приложенные к ещё не отправленному комментарию, — с крестиком. */
export function DraftImages({ state }: { state: CommentImagesState }) {
  if (state.images.length === 0 && state.uploading === 0) return null
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="comment-draft-images">
      {state.images.map((img) => (
        <div key={img.id} className="relative">
          <img
            src={commentImageUrl(img.fileName)}
            alt="Картинка к комментарию"
            className="h-14 w-14 rounded border border-gray-200 object-cover"
          />
          <button
            type="button"
            onClick={() => state.remove(img.id)}
            title="Убрать картинку"
            aria-label="Убрать картинку"
            className="absolute -right-1.5 -top-1.5 h-5 w-5 rounded-full bg-gray-700 text-xs leading-5 text-white hover:bg-gray-900"
          >
            ×
          </button>
        </div>
      ))}
      {state.uploading > 0 && <span className="text-xs text-gray-400">Загружаю картинку…</span>}
    </div>
  )
}

/** Картинки отправленного комментария: миниатюры, по клику — полный размер в новой вкладке. */
export function CommentImages({ images }: { images?: CommentImage[] }) {
  if (!images || images.length === 0) return null
  return (
    <div className="mt-1 flex flex-wrap gap-2" data-testid="comment-images">
      {images.map((img) => (
        <a
          key={img.id}
          href={commentImageUrl(img.fileName)}
          target="_blank"
          rel="noopener noreferrer"
          title="Открыть картинку"
        >
          <img
            src={commentImageUrl(img.fileName)}
            alt="Картинка из комментария"
            loading="lazy"
            className="max-h-40 max-w-[240px] rounded border border-gray-200 object-contain bg-white"
          />
        </a>
      ))}
    </div>
  )
}

function IconClip() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  )
}
